import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Mail, MessageSquare, RefreshCw, Siren } from 'lucide-react';
import {
  DOMAIN_RULE_FREQUENCY_LABELS,
  RULE_CATEGORY_LABELS,
  canHandleAlarm,
  canOpenContract,
  formatDateTime,
  formatRelative,
  type DomainRuleFrequency,
} from '@codepass/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/auth/AuthProvider';
import { Alert, Button, Card, CardBody, CardHeader, CardTitle, Skeleton } from '@/components/ui';
import {
  AlertStatusBadge,
  Money,
  PageHeader,
  SEVERITY_BORDER,
  SeverityBadge,
} from '@/components/domain';
import { AlertActionButtons, AlertActionDialog, useAlertActions } from '@/components/AlertActions';
import { cn } from '@/lib/cn';
import type { AlertDetailItem, AlertEvent } from '@/types';

/**
 * Alarm-detalje. PRD par. 8.2.
 *
 * Siden skal kunne svare paa fire ting uden at man forlader den: hvilken regel der udloeste
 * alarmen, hvilke data der fik den til at trigge, hvad der er sket siden, og hvad man kan gøre
 * nu. De fire er derfor fire paneler - ikke fire steder at klikke hen.
 */

const EVENT_ICONS: Record<AlertEvent['kind'], typeof Siren> = {
  created: Siren,
  updated: RefreshCw,
  status: ArrowRight,
  notification: Mail,
  comment: MessageSquare,
};

export function AlertDetailPage() {
  const { id = '' } = useParams();
  const actions = useAlertActions();
  /* Links kun til sider, rollen kan aabne - en udvikler ser kontraktens navn, ikke kontrakten. */
  const { can, user } = useAuth();

  const {
    data: alert,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['alert', id],
    queryFn: () => api.get<AlertDetailItem>(`/alerts/${id}`),
    enabled: Boolean(id),
  });

  if (isLoading) return <Skeleton className="h-64" />;
  if (error || !alert) return <Alert tone="error">Kunne ikke hente alarmen.</Alert>;

  const triggerData = alert.trigger_data ?? [];
  const events = alert.events ?? [];

  return (
    <>
      <PageHeader
        title={alert.title}
        description={RULE_CATEGORY_LABELS[alert.rule.category]}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {/* Someone else's alarm is read-only without 'Behandl alle alarmer'. */}
            {canHandleAlarm(user, alert.recipient_id) ? (
              <AlertActionButtons
                status={alert.status}
                busy={actions.busy}
                onAct={(action) => actions.run(alert.id, action)}
              />
            ) : null}
            <Link to="/alarmer">
              <Button variant="ghost" size="sm">
                Tilbage
              </Button>
            </Link>
          </div>
        }
      />

      {actions.error ? (
        <Alert tone="error" className="mb-3">
          {actions.error}
        </Alert>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card className={cn('border-l-4', SEVERITY_BORDER[alert.severity])}>
            <CardBody>
              <div className="flex flex-wrap items-center gap-2">
                <SeverityBadge severity={alert.severity} />
                <AlertStatusBadge status={alert.status} />
                <span className="text-xs text-ink-muted">
                  Oprettet {formatRelative(alert.created_at)}
                </span>
                {alert.snoozed_until ? (
                  <span className="text-xs text-ink-muted">
                    · Kommer tilbage {formatDateTime(alert.snoozed_until)}
                  </span>
                ) : null}
              </div>

              <p className="mt-3 text-sm">{alert.description}</p>

              {alert.suggested_action ? (
                <div className="mt-4 rounded-md bg-brand-50 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-brand-800">
                    Foreslået handling
                  </p>
                  <p className="mt-1 text-sm">{alert.suggested_action}</p>
                  {alert.suggested_amount_excl_vat !== null ? (
                    <p className="mt-1 text-sm font-medium">
                      <Money
                        value={alert.suggested_amount_excl_vat}
                        currency={alert.contract?.currency ?? 'DKK'}
                      />
                    </p>
                  ) : null}
                </div>
              ) : null}

              {alert.resolution_note ? (
                <div className="mt-4 rounded-md bg-canvas p-3">
                  <p className="text-xs font-semibold">Løsningsnote</p>
                  <p className="mt-1 text-sm">{alert.resolution_note}</p>
                  <p className="mt-1 text-xs text-ink-muted">
                    {alert.resolved_by?.name ?? 'System'}
                    {alert.resolved_at ? ` · ${formatDateTime(alert.resolved_at)}` : ''}
                  </p>
                </div>
              ) : null}
            </CardBody>
          </Card>

          {/*
            Udloesende data. PRD par. 8.2 giver eksemplet "Timer registreret: 82, kontrakt-loft:
            80" - alarmen skal kunne efterproeves uden at nogen skal slaa tallene op selv.
          */}
          {triggerData.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Udløsende data</CardTitle>
              </CardHeader>
              <CardBody>
                <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
                  {triggerData.map((fact) => (
                    <div
                      key={fact.label}
                      className="flex items-baseline justify-between gap-3 border-b border-line pb-1"
                    >
                      <dt className="text-xs text-ink-muted">{fact.label}</dt>
                      <dd className="tabular text-sm font-medium">{fact.value}</dd>
                    </div>
                  ))}
                </dl>
              </CardBody>
            </Card>
          ) : null}

          {/*
            Ingen kommentarer. En alarm er et forhold, der skal handles på, og behandlingen er
            statussen plus begrundelsen — der er hverken en tabel eller en rute til andet. Kortet
            stod her med et tekstfelt, og et felt man kan skrive i, men ikke gemme, er værre end
            ingenting.
          */}
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Regel</CardTitle>
            </CardHeader>
            <CardBody className="space-y-2 text-sm">
              <p className="font-medium">{alert.rule_detail.title}</p>
              <p className="text-xs text-ink-muted">{alert.rule_detail.description}</p>
              <dl className="mt-3 space-y-1 text-xs">
                <div className="flex justify-between gap-2">
                  <dt className="text-ink-muted">Teknisk navn</dt>
                  <dd className="font-mono">{alert.rule_detail.name}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-ink-muted">Kategori</dt>
                  <dd>{RULE_CATEGORY_LABELS[alert.rule_detail.category]}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-ink-muted">Kører</dt>
                  <dd>{frequencyLabel(alert.rule_detail.frequency)}</dd>
                </div>
              </dl>

              {can('rules.view') ? (
                <Link
                  to="/admin/regler"
                  className="mt-3 inline-block text-xs text-brand hover:underline"
                >
                  Justér reglen
                </Link>
              ) : null}
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Kontekst</CardTitle>
            </CardHeader>
            <CardBody className="space-y-2 text-sm">
              {alert.contract ? (
                <p>
                  <span className="text-ink-muted">Kontrakt: </span>
                  {canOpenContract(user, alert.contract) ? (
                    <Link to={`/kontrakter/${alert.contract.id}`} className="hover:underline">
                      {alert.contract.title}
                    </Link>
                  ) : (
                    alert.contract.title
                  )}
                </p>
              ) : null}
              {alert.customer ? (
                <p>
                  <span className="text-ink-muted">Kunde: </span>
                  {can('customers.view') ? (
                    <Link to={`/kunder/${alert.customer.id}`} className="hover:underline">
                      {alert.customer.name}
                    </Link>
                  ) : (
                    alert.customer.name
                  )}
                </p>
              ) : null}
              <p className="text-xs text-ink-muted">
                Notifikation sendt: {alert.notification_sent ? 'Ja' : 'Nej'}
              </p>
              {/* Dedup-nøglen er teknisk, men uvurderlig når man skal forstå hvorfor der er
                  én alarm og ikke tre. */}
              <p className="break-all font-mono text-xs text-ink-muted">{alert.dedup_key}</p>
            </CardBody>
          </Card>

          {/*
            Historikken. PRD par. 8.2: oprettet, notifikationer sendt, kommentarer - i ét spor,
            saa man kan laese forloebet fremad frem for at stykke det sammen af tre paneler.
          */}
          {events.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Historik</CardTitle>
              </CardHeader>
              <CardBody>
                <ol className="space-y-3">
                  {events.map((event) => {
                    const Icon = EVENT_ICONS[event.kind];
                    return (
                      <li key={event.id} className="flex gap-3">
                        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
                        <div className="min-w-0">
                          <p className="text-xs">{event.message}</p>
                          <p className="text-xs text-ink-muted">
                            {formatDateTime(event.at)}
                            {event.actor ? ` · ${event.actor}` : ''}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </CardBody>
            </Card>
          ) : null}
        </div>
      </div>

      <AlertActionDialog {...actions.dialogProps} />
    </>
  );
}

/**
 * Frekvensen kommer fra regelraekken og er dansk. Ukendte vaerdier vises raat frem for at
 * forsvinde: en ny frekvens i tabellen skal kunne ses, ikke skjules.
 */
function frequencyLabel(frequency: string | undefined): string {
  if (!frequency) return 'Ukendt';
  return DOMAIN_RULE_FREQUENCY_LABELS[frequency as DomainRuleFrequency] ?? frequency;
}
